import { useClickOutside } from '@mantine/hooks';
import { format } from 'date-fns';
import type { FC } from 'react';
import { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker, type DateRange as DayPickerRange } from 'react-day-picker';

export type UiDateRange = { startDate: string; endDate: string };

type DateSinglePickerProps = {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    clearable?: boolean;
};

export const DateSinglePicker: FC<DateSinglePickerProps> = ({
    value,
    onChange,
    placeholder = 'Pick a date',
    clearable = false,
}) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const display = value ? format(new Date(value), 'dd MMM yyyy') : '';

    return (
        <>
            <button
                ref={anchorRef}
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="flex h-[34px] w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 text-[13px] text-slate-700 hover:border-slate-400 focus:border-slate-900 focus:outline-none"
            >
                <span className={display ? '' : 'text-slate-400'}>
                    {display || placeholder}
                </span>
                {clearable && display ? (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange('');
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                onChange('');
                            }
                        }}
                        className="cursor-pointer rounded-full px-1 text-[11px] text-slate-400 hover:text-slate-700"
                    >
                        ✕
                    </span>
                ) : null}
            </button>
            {open ? (
                <DateSinglePopover
                    anchorRef={anchorRef}
                    value={value}
                    onApply={(next) => {
                        onChange(next);
                        setOpen(false);
                    }}
                    onClose={() => setOpen(false)}
                />
            ) : null}
        </>
    );
};

type DateSinglePopoverProps = {
    anchorRef: React.RefObject<HTMLButtonElement | null>;
    value: string;
    onApply: (value: string) => void;
    onClose: () => void;
};

const DateSinglePopover: FC<DateSinglePopoverProps> = ({
    anchorRef,
    value,
    onApply,
    onClose,
}) => {
    const popoverRef = useClickOutside(() => onClose());
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const initial = value ? new Date(value) : undefined;

    useLayoutEffect(() => {
        const el = anchorRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const popoverWidth = 320;
        const popoverHeight = 380;
        const margin = 16;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const rawLeft = rect.left;
        const left =
            rawLeft + popoverWidth > viewportWidth - margin
                ? viewportWidth - popoverWidth - margin
                : rawLeft;
        // Open below the field by default; flip above it (or clamp into view)
        // when there isn't room below, so the calendar is never pushed off the
        // bottom of the viewport.
        let top = rect.bottom + 6;
        if (top + popoverHeight > viewportHeight - margin) {
            const above = rect.top - 6 - popoverHeight;
            top =
                above >= margin
                    ? above
                    : Math.max(margin, viewportHeight - popoverHeight - margin);
        }
        setPos({ top, left });
    }, [anchorRef]);

    if (!pos) return null;

    return createPortal(
        <div
            ref={popoverRef as React.RefObject<HTMLDivElement>}
            style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                zIndex: 9999,
            }}
            className="w-[320px] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl"
        >
            <div className="mb-3 flex items-center justify-between text-xs font-medium text-slate-700">
                <span>Select date</span>
                <button
                    type="button"
                    onClick={onClose}
                    className="cursor-pointer rounded-full px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-100"
                >
                    ✕
                </button>
            </div>

            <DayPicker
                mode="single"
                selected={initial}
                onSelect={(date) => {
                    if (date) onApply(format(date, 'yyyy-MM-dd'));
                }}
                defaultMonth={initial ?? new Date()}
                weekStartsOn={0}
                showOutsideDays
                fixedWeeks
                className="reparo-range-picker"
                modifiersClassNames={{ selected: 'rp-single-selected' }}
            />
        </div>,
        document.body,
    );
};

type DatePopoverProps = {
    range: UiDateRange;
    onApply: (range: UiDateRange) => void;
    onClose: () => void;
};

export const DatePopover: FC<DatePopoverProps> = ({
    range,
    onApply,
    onClose,
}) => {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const popoverRef = useClickOutside(() => onClose());
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

    useLayoutEffect(() => {
        if (sentinelRef.current) {
            const rect = sentinelRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const popoverWidth = 640;
            const popoverHeight = 420;
            const margin = 16;

            const rawLeft = rect.left;
            const left =
                rawLeft + popoverWidth > viewportWidth - margin
                    ? viewportWidth - popoverWidth - margin
                    : rawLeft;

            // Flip above / clamp into view so the calendar is never pushed off
            // the bottom of the viewport.
            let top = rect.top + 8;
            if (top + popoverHeight > viewportHeight - margin) {
                const above = rect.top - 8 - popoverHeight;
                top =
                    above >= margin
                        ? above
                        : Math.max(
                              margin,
                              viewportHeight - popoverHeight - margin,
                          );
            }

            setPos({ top, left });
        }
    }, []);

    const initialRange: DayPickerRange | undefined = {
        from: range.startDate ? new Date(range.startDate) : undefined,
        to: range.endDate ? new Date(range.endDate) : undefined,
    };

    const [selectedRange, setSelectedRange] = useState<
        DayPickerRange | undefined
    >(initialRange);

    const handleApply = () => {
        if (!selectedRange?.from || !selectedRange.to) {
            onClose();
            return;
        }
        onApply({
            startDate: format(selectedRange.from, 'yyyy-MM-dd'),
            endDate: format(selectedRange.to, 'yyyy-MM-dd'),
        });
    };

    const popover = pos ? (
        <div
            ref={popoverRef as React.RefObject<HTMLDivElement>}
            style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                zIndex: 9999,
            }}
            className="w-[640px] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl"
        >
            <div className="mb-3 flex items-center justify-between text-xs font-medium text-slate-700">
                <span>Select date range</span>
                <button
                    type="button"
                    onClick={onClose}
                    className="cursor-pointer rounded-full px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-100"
                >
                    ✕
                </button>
            </div>

            <DayPicker
                mode="range"
                selected={selectedRange}
                onSelect={setSelectedRange}
                numberOfMonths={2}
                defaultMonth={selectedRange?.from ?? new Date()}
                weekStartsOn={0}
                showOutsideDays
                fixedWeeks
                className="reparo-range-picker"
                modifiersClassNames={{
                    selected: 'rp-selected',
                    range_start: 'rp-range-start',
                    range_end: 'rp-range-end',
                    range_middle: 'rp-range-middle',
                }}
            />

            <div className="mt-4 flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onClose}
                    className="cursor-pointer rounded-full border border-slate-200 px-3 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={handleApply}
                    className="cursor-pointer rounded-full bg-slate-900 px-3 py-1 text-[11px] font-medium text-white"
                >
                    Apply
                </button>
            </div>
        </div>
    ) : null;

    return (
        <>
            {/* zero-size sentinel: sits in normal DOM flow to measure where the popover should appear */}
            <div ref={sentinelRef} style={{ height: 0, width: 0 }} />
            {createPortal(popover, document.body)}
        </>
    );
};

/** "Mar 04 - 21" when both ends share a month, "Mar 04 - Apr 02" otherwise. */
const formatRangeLabel = (range: UiDateRange): string => {
    if (!range.startDate || !range.endDate) return 'Select dates';
    const start = new Date(range.startDate);
    const end = new Date(range.endDate);
    const sameMonth =
        start.getMonth() === end.getMonth() &&
        start.getFullYear() === end.getFullYear();
    return sameMonth
        ? `${format(start, 'MMM dd')} - ${format(end, 'dd')}`
        : `${format(start, 'MMM dd')} - ${format(end, 'MMM dd')}`;
};

type DateRangePickerProps = {
    value: UiDateRange;
    onChange: (value: UiDateRange) => void;
    /** When provided, renders a reset affordance next to the label. */
    onReset?: () => void;
    /** Tooltip on the trigger button. */
    title?: string;
};

/**
 * Trigger button + range popover. The range counterpart to DateSinglePicker,
 * for callers that would otherwise hand-roll a button around DatePopover.
 */
export const DateRangePicker: FC<DateRangePickerProps> = ({
    value,
    onChange,
    onReset,
    title,
}) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                title={title}
                className="flex h-[30px] cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-700 hover:border-slate-300"
            >
                <span>📅</span>
                <span>{formatRangeLabel(value)}</span>
                {onReset ? (
                    <span
                        role="button"
                        tabIndex={0}
                        title="Back to the page date range"
                        onClick={(e) => {
                            e.stopPropagation();
                            onReset();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                onReset();
                            }
                        }}
                        className="cursor-pointer rounded-full px-1 text-[11px] text-slate-400 hover:text-slate-700"
                    >
                        ✕
                    </span>
                ) : null}
            </button>
            {open ? (
                <DatePopover
                    range={value}
                    onApply={(next) => {
                        onChange(next);
                        setOpen(false);
                    }}
                    onClose={() => setOpen(false)}
                />
            ) : null}
        </>
    );
};
